import {
  Card,
  CardContent,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DeleteIcon from '@material-ui/icons/Delete';
import { Edit } from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { useEffect, useState } from 'react';
import create, { UseStore } from 'zustand';
import {
  Expansion,
  ExpansionDetails,
  ExpansionSummary,
  formatISO,
} from '../../core';
import { auth } from '../../core/auth';
import { Auto, Text } from '../../core/controls';
import { formatLocalDate } from '../../core/date-time';
import { CoderNote, Person, Worksheet } from '../../core/domain';
import { FormApi, formApi } from '../../core/form';
import { HospitalistContext } from '../../core/hospitalist-context';
import {
  addCoderNoteRequest,
  deleteCoderNoteteRequest,
} from '../../core/notes-api';
import { darkBlue, mediumGray } from '../../core/theme';
import { WorksheetApis } from '../../core/visit-context';
export interface NoteLists {
  [key: string]: CoderNote[];
}

type NotesApi = {
  notes: NoteLists;
  note: CoderNote;
  context: string;
  contexts: string[];
  open: boolean;
  addNote(context: string, note: CoderNote): void;
  removeNote(context: string, note: CoderNote, hardDelete?: boolean): void;
  setNote(value: CoderNote): void;
  setNotes(context: string, note: CoderNote[]): void;
  openDialog(
    contexts: string | string[],
    note?: CoderNote,
    onAdded?: (n: CoderNote) => void,
    onRemoved?: (n: CoderNote) => void
  ): void;
  closeDialog(): void;
  nNotes(ctx?: string | string[]): number;
  groupById(
    id: (n: CoderNote) => string,
    label: (key: string) => string,
    context: string
  ): { label: string; list: CoderNote[]; id: string }[];
};

interface Ids {
  [key: string]: string;
}
interface NotesProps {
  notes?: NoteLists;
  ids?: Ids;
}

export const NO_NOTE_CONTEXT = 'CPT';

export function emptyNotes(contexts: string[]): NoteLists {
  const notes = {};
  contexts.forEach((key) => (notes[key] = []));
  return notes;
}

export interface UseNotesApi {
  useNotes: UseStore<NotesApi>;
  ids?: Ids;
  apis?: WorksheetApis[];
  selectedWorksheet?: () => string;
}

export function notesApi(props?: NotesProps): UseNotesApi {
  const { notes, ids } = { ...props };

  const validateContext = (context: string) => {
    if (context != NO_NOTE_CONTEXT && !notes[context]) {
      notes[context] = [];
    }
  };
  let onAdded: (n: CoderNote) => void = null;
  let onRemoved: (n: CoderNote) => void = null;
  let edit = false;

  function groupById(
    id: (n: CoderNote) => string,
    label: (key: string) => string,
    context: string
  ) {
    const map = {};
    const list = notes[context] || [];
    list.forEach((a: CoderNote) => {
      const key = id(a);
      map[key] = map[key] || [];
      map[key].push(a);
    });

    return Object.entries(map).map(([k, v]) => {
      return {
        label: label(k),
        list: v as CoderNote[],
        id: k,
      };
    });
  }
  const useNotes = create<NotesApi>((set, get) => ({
    notes,
    note: null,
    context: null,
    contexts: null,
    open: false,
    groupById,
    addNote: (context, note) => {
      validateContext(context);
      if (!edit) {
        note.noteDate = note.noteDate || new Date();

        if (context !== NO_NOTE_CONTEXT) {
          notes[context].push(note);
        }

        set({ notes: { ...notes } });

        if (onAdded) {
          onAdded(note);
        }
        edit = false;
      } else {
        let existing = get().note;

        if (context !== NO_NOTE_CONTEXT) {
          existing = notes[context].filter((v) => v === existing)[0];
          if (existing) {
            existing.note = note.note;
          }
        } else {
          if (onAdded) {
            onAdded(note);
          }
        }
      }
    },
    removeNote: (context, note, hardDelete = true) => {
      if (onRemoved) {
        onRemoved(note);
      }
      validateContext(context);
      if (notes[context]) {
        note.active = false;
        if (hardDelete) {
          notes[context].splice(notes[context].indexOf(note), 1);
        }
      }
      set({ notes: { ...notes } });
    },
    setNote: (note) => set({ note }),
    setNotes: (context, values) => {
      validateContext(context);

      if (!notes[context]) {
        notes[context] = [];
      }
      if (values && values.length && values[0]) {
        notes[context].push(...values);
      }
    },
    openDialog: (contexts, note, afn, rfn) => {
      let context: string;
      let contextList: string[];

      onAdded = afn;
      onRemoved = rfn;
      edit = new Boolean(note).valueOf();

      if (Array.isArray(contexts)) {
        contextList = contexts;
        context = contexts[0];
      } else {
        context = contexts;
      }
      validateContext(context);

      set({
        context,
        contexts: contextList,
        note: note,
        open: true,
      });
    },
    closeDialog: () => set({ open: false }),
    nNotes: (ctx: string | string[]) => {
      const list = Array.isArray(ctx) ? ctx : [ctx];
      let result = 0;

      list.forEach((context) => {
        if (!notes[context]) {
          return;
        }
        result += notes[context].length;
      });
      return result;
    },
  }));
  return { useNotes, ids };
}

export function worksheetNotes(worksheets: Worksheet[]): UseNotesApi {
  const ids = {};
  const worksheetTypes = [];

  worksheets.forEach((w) => {
    worksheetTypes.push(w.worksheetType);
    ids[w.worksheetType] = w.worksheetId;
  });

  const api = notesApi({ notes: emptyNotes(worksheetTypes), ids });

  return api;
}

const cardStyles = makeStyles({
  root: {
    width: '100%',
    minWidth: 180,
  },
  header: {
    fontSize: 12,
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)',
    color: mediumGray,
  },
  light: {
    color: mediumGray,
  },
  message: {
    paddingTop: '12px',
    whiteSpace: 'pre-line',
  },
});

function isSignedInUser(a: Person): boolean {
  if (!a?.firstName) {
    return true;
  }
  const { firstName, lastName } = auth.signedIn || {}
  return a.firstName === firstName && a.lastName === lastName;
}

interface NoteProps {
  coderNote: CoderNote;
  api: UseNotesApi;
  context: string;
  disabled?: boolean;
}
export function Note(props: NoteProps) {
  const { coderNote, disabled } = props;
  const { note, noteDate, author, worksheetId, coderNoteId, active } =
    coderNote;
  const classes = cardStyles();
  const bull = <span className={classes.bullet}>•</span>;

  const { api, context } = props;

  async function deleteNote() {
    if (coderNoteId) {
      await deleteCoderNoteteRequest({ worksheetId, coderNoteId });
    }
    api.useNotes.getState().removeNote(context, coderNote);
  }

  let person = '';
  if (author?.firstName) {
    person = `${author.firstName} ${author.lastName}`;
  }

  const deleted = !active;
  const timestamp = `${formatISO(noteDate)} ${deleted ? ' - Deleted' : ''}`;
  const deletedStyle: any = deleted
    ? {
        textDecoration: 'line-through',
        overflowWrap: 'anywhere',
      }
    : { overflowWrap: 'anywhere' };

  const disableDelete = !isSignedInUser(author) || !active || disabled;

  return (
    <Card
      className={classes.root}
      style={{ backgroundColor: deleted ? '#f8f8f8' : undefined }}
    >
      <CardContent>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '10px',
            paddingRight: '0px',
          }}
        >
          <Typography className={classes.header} color="textPrimary">
            {person} {bull} <span className={classes.light}> {timestamp}</span>
          </Typography>
          <div style={{ flexGrow: 1 }} />
          <IconButton
            disabled={disableDelete}
            onClick={deleteNote}
            style={{ width: '10px', height: '10px' }}
          >
            <DeleteIcon />
          </IconButton>
        </div>

        <Typography
          className={classes.message}
          style={deletedStyle}
          color="textSecondary"
          gutterBottom
        >
          {note}
        </Typography>
      </CardContent>
    </Card>
  );
}


export function NoteDetails(props: NoteProps) {
  const { coderNote, disabled } = props;
  const { note, noteDate, author, worksheetId, coderNoteId, active } =
    coderNote;
  const classes = cardStyles();
  const bull = <span className={classes.bullet}>•</span>;

  const { api, context } = props;

  let person = '';
  if (author?.firstName) {
    person = `${author.firstName} ${author.lastName}`;
  }

  const deleted = !active;
  const timestamp = `${formatISO(noteDate)} ${deleted ? ' - Deleted' : ''}`;
  const deletedStyle: any = deleted
    ? {
        textDecoration: 'line-through',
        overflowWrap: 'anywhere',
      }
    : { overflowWrap: 'anywhere' };


  return (
    <Card
      className={classes.root}
      style={{ backgroundColor: deleted ? '#f8f8f8' : undefined }}
    >
      <CardContent>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '10px',
            paddingRight: '0px',
          }}
        >
          <Typography className={classes.header} color="textPrimary">
            {person} {bull} <span className={classes.light}> {timestamp}</span>
          </Typography>
          <div style={{ flexGrow: 1 }} />
        </div>

        <Typography
          className={classes.message}
          style={deletedStyle}
          color="textSecondary"
          gutterBottom
        >
          {note}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function NotesPage(props: UseNotesApi) {
  const { useNotes } = props;
  const notes = useNotes((s) => s.notes);
  const noteContexts = Object.entries(notes);

  const containerStyle = { width: '103%', padding: '12px 18px' };

  return (
    <div style={containerStyle}>
      <Grid container direction="row" spacing={2} style={{ width: '100%' }}>
        {noteContexts.map((e) => (
          <Grid
            container
            key={e[0]}
            item
            direction="column"
            spacing={1}
            style={{ width: '50%' }}
          >
            <Grid item key={e[0] + 'title'} style={{ paddingLeft: '0px' }}>
              <Typography variant="subtitle2" style={{ padding: '0px' }}>
                NOTES
              </Typography>
            </Grid>
            {e[1].map((v, i) => (
              <Grid item key={e[0] + i} style={{ paddingLeft: '0px' }}>
                <Note coderNote={v} context={e[0] as string} api={props} />
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

function WorksheetGroup({ label, context, notes, api, selected, ...rest }) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(rest.expanded), [rest.expanded]);

  return (
    <Grid item key={label}>
      <Expansion expanded={expanded}>
        <ExpansionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={() => setExpanded(!expanded)}
        >
          <Grid
            container
            direction="row"
            style={{ flexWrap: 'nowrap' }}
            spacing={1}
            alignItems="center"
          >
            {selected && (
              <Grid item>
                <Edit fontSize="small" htmlColor={darkBlue} />
              </Grid>
            )}
            <Grid item>
              <Typography color="textSecondary">{label}</Typography>
            </Grid>
          </Grid>
        </ExpansionSummary>

        <ExpansionDetails>
          <Grid container direction="column" key={label}>
            {notes.map((v: CoderNote, i: any) => (
              <>
                <Grid item key={label + i} style={{ paddingLeft: '0px' }}>
                  <Note
                    coderNote={v}
                    context={context}
                    api={api}
                    disabled={!selected}
                  />
                </Grid>
                <br />
              </>
            ))}
          </Grid>
        </ExpansionDetails>
      </Expansion>
    </Grid>
  );
}
type NotesTabProps = {
  api: UseNotesApi;
  hospitalistContext: HospitalistContext;
};
export function NotesTab(props: NotesTabProps) {
  const { hospitalistContext } = props;
  const { useNotes } = props.api;
  const notes = useNotes((s) => s.notes);
  const noteContexts = Object.entries(notes);

  const containerStyle = {
    width: '100%',
    paddingLeft: 24,
    paddingTop: 12,
    paddingBottom: 12,
  };

  function worksheetLabel(id: string) {
    let w: any;
    if (hospitalistContext) {
      const a = hospitalistContext.apisById(id);
      w = a?.context?.worksheet;
    }
    if (!w) {
      return 'Coder Notes';
    }
    return `${w.worksheetType} - ${formatLocalDate(w.serviceDate)}`;
  }

  const noteApi = useNotes.getState();
  function worksheetGroup(context: string) {
    return noteApi.groupById((a) => a.worksheetId, worksheetLabel, context);
  }

  let contextGroups: [string, any[]][] = noteContexts.map((ctx) => [
    ctx[0],
    worksheetGroup(ctx[0]),
  ]);

  let defaultSelection: any = contextGroups?.length
    ? contextGroups[0][1]
    : null;

  if (defaultSelection) {
    defaultSelection = defaultSelection[0] && defaultSelection[0].id;
  }

  let selectedId: string;
  if (hospitalistContext) {
    selectedId =
      hospitalistContext.data((s) => s.selectedId) || defaultSelection;
    const api = hospitalistContext.apisById(selectedId);
    contextGroups = contextGroups.filter(
      (a) => a[0] === api.context.worksheet.worksheetType
    );

    if (contextGroups.length) {
      const ctx = contextGroups[0];
      const first = ctx[1].find((a) => a.id === selectedId);
      const rest = ctx[1].filter((a) => a.id !== selectedId);
      if (first) {
        ctx[1] = [first].concat(rest);
      }
    }
  }

  return (
    <div style={containerStyle}>
      <Grid
        container
        direction="column"
        spacing={2}
        style={{
          width: '100%',
          flexWrap: 'nowrap',
          height: '83vh',
          overflowY: 'scroll',
        }}
      >
        {contextGroups.map((v: [string, any[]]) => (
          <Grid
            container
            key={v[0]}
            item
            direction="column"
            spacing={1}
            style={{ width: '100%' }}
          >
            {v[1].map((v: any, i) => (
              <WorksheetGroup
                key={'g' + i}
                context={v[0]}
                label={v.label}
                notes={v.list}
                api={props.api}
                selected={selectedId === v.id || !hospitalistContext}
                expanded={selectedId === v.id || !hospitalistContext}
              />
            ))}
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

export function AddNoteDialog(props: UseNotesApi) {
  const { useNotes, ids, selectedWorksheet } = props;
  const open = useNotes((s) => s.open);
  const existingNote = useNotes((s) => s.note);
  const existingNoteValue = (existingNote && existingNote.note) || '';
  const contexts = useNotes((s) => s.contexts);
  const addNote = useNotes((s) => s.addNote);
  const context = useNotes((s) => s.context);
  const hasContexts =
    context !== NO_NOTE_CONTEXT && contexts && contexts.length > 0;
  const showWorsheetTypes = contexts?.length > 1;

  const initialValues = { note: '', ctx: 0 };

  const closeDialog = useNotes((s) => s.closeDialog);

  const [disabled, setDisabled] = useState(false);
  const form: FormApi = formApi({
    values: {
      note: '',
      ctx: hasContexts ? contexts[0] : ('' as any),
    },
    fields: {
      note: {
        label: 'Note',
        required: true,
        style: { width: '100%' },
        multiline: true,
        rows: 8,
      },
      ctx: {
        label: '',
        list: hasContexts ? contexts.map((value) => value) : [],
        listLabel: (v) => v,
        style: { width: '160px' },
      },
    },
    valid: (v) => v?.note?.length > 3,
    onSubmit: async (v) => {
      setDisabled(true);

      let formContext = context;
      if (hasContexts && context !== NO_NOTE_CONTEXT && v.ctx) {
        formContext = v.ctx;
      }
      const worksheetId = selectedWorksheet
        ? selectedWorksheet()
        : hasContexts
        ? ids[formContext]
        : null;

      let newNote = {
        ...existingNote,
        ...{ note: note.value.getState().value, context: formContext },
        ...{ worksheetId },
      };
      newNote.author = newNote.author || auth.signedIn;

      if (context !== NO_NOTE_CONTEXT && !existingNote) {
        const r = await addCoderNoteRequest({
          worksheetId,
          note: newNote.note,
        });
        
        newNote = { ...newNote, ...r.result, ...{ worksheetId } };
        newNote.active = true;
      }
      addNote(formContext, newNote);
      closeDialog();
      setDisabled(false);
    },
    resetValues: () => initialValues,
  });

  const { note, ctx } = form.controls;

  useEffect(() => {
    const formValue = note.value.getState().value;
    if (!formValue && existingNoteValue) {
      note.value.getState().setValue(existingNoteValue);
      form.setValues({ note: existingNoteValue });
    }
  });

  const onCancel = () => {
    note.value.getState().setValue('');
    closeDialog();
  };
  const onAdd = () => {
    form.submit();
  };

  const deleteNote = async () => {
    note.value.getState().setValue('');
    useNotes.getState().removeNote(context, { note: '' });
    closeDialog();
  };
  return open ? (
    <Dialog open={open}>
      <DialogTitle>
        <div
          style={
            {
              display: 'flex',
              direction: 'row',
              alignItems: 'flex-end',
              height: '30px',
            } as any
          }
        >
          {showWorsheetTypes ? (
            <>
              <div style={{ paddingRight: '16px' }}>Add Coder Note</div>
              <Auto {...ctx} />
            </>
          ) : (
            <>
              <div>Add Note</div>
              <div style={{ flexGrow: 1 }}></div>
              <IconButton
                onClick={deleteNote}
                style={{ width: '10px', height: '10px' }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </div>
      </DialogTitle>

      <DialogContent style={{ zIndex: 1501 }}>
        <Grid
          container
          spacing={2}
          direction="column"
          style={{ width: '400px' }}
        >
          <Grid item xs={12}>
            <Text {...note} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button
          onClick={onAdd}
          disabled={disabled}
          color="primary"
          variant="contained"
        >
          {' '}
          Add{' '}
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <></>
  );
}
